import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/AdminLeagues.css';

function AdminLeagues() {
  const { user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    if (!user?.isSiteAdmin) return;
    fetchLeagues();
  }, [user]);

  const fetchLeagues = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/leagues`);
      setLeagues(response.data.leagues);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch leagues');
    } finally {
      setLoading(false);
    }
  };

  const ViewButton = useCallback((params) => (
    <button
      className="btn-view-league"
      onClick={() => navigate(`/admin/leagues/${params.data._id}`)}
    >
      View
    </button>
  ), [navigate]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'League Name',
      field: 'name',
      sortable: true,
      filter: true,
      minWidth: 180,
    },
    {
      headerName: 'Commissioner',
      valueGetter: (params) => {
        const c = params.data.commissioner;
        if (!c) return '—';
        return c.firstName ? `${c.firstName} ${c.lastName || ''}`.trim() : c.username;
      },
      sortable: true,
      filter: true,
      minWidth: 150,
    },
    {
      headerName: 'Tournament',
      valueGetter: (params) => params.data.tournament?.name || '—',
      sortable: true,
      filter: true,
      minWidth: 200,
    },
    {
      headerName: 'Surface',
      valueGetter: (params) => params.data.tournament?.surface || '—',
      sortable: true,
      filter: true,
      minWidth: 100,
    },
    {
      headerName: 'Managers',
      valueGetter: (params) => `${params.data.managers?.length || 0} / ${params.data.maxManagers}`,
      sortable: true,
      filter: true,
      minWidth: 110,
    },
    {
      headerName: 'Draft Status',
      field: 'draftStatus',
      sortable: true,
      filter: true,
      minWidth: 130,
      cellStyle: (params) => {
        if (params.value === 'drafting') return { color: '#27ae60', fontWeight: 600 };
        if (params.value === 'complete') return { color: '#667eea', fontWeight: 600 };
        return { color: '#888' };
      },
    },
    {
      headerName: 'Budget',
      field: 'draftBudget',
      sortable: true,
      filter: true,
      valueFormatter: (params) => `${params.value} cr`,
      minWidth: 90,
    },
    {
      headerName: 'Created',
      field: 'createdAt',
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : '—',
      minWidth: 120,
    },
    {
      headerName: 'Actions',
      cellRenderer: ViewButton,
      sortable: false,
      filter: false,
      minWidth: 90,
    },
  ], [ViewButton]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    flex: 1,
  }), []);

  if (!user?.isSiteAdmin) {
    return (
      <div className="admin-leagues-page">
        <p className="admin-error">Access denied. Site Admin only.</p>
      </div>
    );
  }

  return (
    <div className="admin-leagues-page">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="admin-leagues-content">
        <header className="admin-leagues-page-header">
          <h2>🛡️ Admin — All Leagues</h2>
          {!loading && <p>{leagues.length} league{leagues.length !== 1 ? 's' : ''} on the platform</p>}
        </header>

        {loading && <p className="admin-status-msg">Loading leagues...</p>}
        {error && <p className="admin-status-msg admin-error-msg">{error}</p>}

        {!loading && !error && (
          <div className="admin-leagues-section">
            <div className="ag-theme-alpine admin-grid">
              <AgGridReact
                rowData={leagues}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={20}
                domLayout="autoHeight"
                animateRows={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminLeagues;
